/**
 * Invoices (S-A51). Wired to the real `InvoiceViewSet`.
 *
 * Server-side filters, which are an allowlist: `status` (repeatable), `client`,
 * `case`, `overdue=true`, `outstanding=true`, plus `search` against `invoice_number`.
 * Anything else is a 400, not a silent ignore.
 *
 * Two separations show up in what this screen offers:
 *
 *   An Account Executive may DRAFT an invoice but never issue one. Quoting is sales;
 *   issuing is finance. `_actions` decides, per record.
 *
 *   Whoever requests money does not confirm it arrived. Verification lives on the
 *   payment queue, not here.
 *
 * Money comes off the wire as decimal strings and stays that way. Parsing into a
 * float loses precision at exactly the magnitudes a ledger cares about; the only
 * numeric comparison below is against zero, which is safe.
 */

import { STAFF, qk, usePaginatedList, type InvoiceListItem } from '@legalcrm/api';
import {
  Badge,
  Button,
  Input,
  Select,
  StatusPill,
  Table,
  statusTone,
  type Column,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useCan } from '@/auth/permissions';
import { formatDate, formatMoney, humanise, isPast } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { NewInvoiceDialog } from './NewInvoiceDialog';

/** Mirrors `Invoice.Status` in `domains/finance/models.py`. */
const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'sent', label: 'Sent' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'credited', label: 'Credited' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'closed', label: 'Closed' },
];

const SCOPE_OPTIONS = [
  { value: '', label: 'All invoices' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'overdue', label: 'Overdue' },
];

export function InvoiceListPage() {
  const navigate = useNavigate();
  const canCreate = useCan('invoice', 'create');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('');
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<InvoiceListItem>(qk.list('invoices'), STAFF.invoices, {
    search: debounced || undefined,
    status: status || undefined,
    // The backend reads these as the literal string "true"; omit them otherwise.
    outstanding: scope === 'outstanding' ? 'true' : undefined,
    overdue: scope === 'overdue' ? 'true' : undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [debounced, status, scope, resetCursor]);

  const columns: Column<InvoiceListItem>[] = [
    {
      key: 'invoice_number',
      header: 'Number',
      width: 'w-40',
      render: (row) => (
        <Link
          to={`/invoices/${row.id}`}
          onClick={(event) => event.stopPropagation()}
          className="font-medium text-primary hover:underline"
        >
          {row.invoice_number}
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusPill label={humanise(row.status)} tone={statusTone(row.status)} />
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      hideOnMobile: true,
      render: (row) => (
        <span className="tabular-nums">{formatMoney(row.total, row.currency)}</span>
      ),
    },
    {
      key: 'amount_paid',
      header: 'Paid',
      align: 'right',
      hideOnMobile: true,
      render: (row) => (
        <span className="tabular-nums text-ink-muted">
          {formatMoney(row.amount_paid, row.currency)}
        </span>
      ),
    },
    {
      key: 'balance',
      header: 'Outstanding',
      align: 'right',
      render: (row) => {
        const owed = Number(row.balance) > 0;
        return (
          <span className={owed ? 'font-medium tabular-nums text-ink' : 'tabular-nums text-ink-muted'}>
            {formatMoney(row.balance, row.currency)}
          </span>
        );
      },
    },
    {
      key: 'due_date',
      header: 'Due',
      align: 'right',
      render: (row) => {
        // Overdue money is what a principal opens this screen for, so it is the one
        // thing on the row allowed to shout.
        const overdue = isPast(row.due_date) && Number(row.balance) > 0;
        return row.due_date ? (
          <span className={overdue ? 'font-medium text-danger' : 'text-ink-muted'}>
            {formatDate(row.due_date)}
          </span>
        ) : (
          <span className="text-ink-subtle">—</span>
        );
      },
    },
    {
      key: 'issue_date',
      header: 'Issued',
      align: 'right',
      hideOnMobile: true,
      render: (row) =>
        row.issue_date ? (
          <span className="tabular-nums">{formatDate(row.issue_date)}</span>
        ) : (
          <Badge tone="neutral" size="sm">
            Not issued
          </Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Invoices"
        description="What has been billed, and what is still owed."
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/payments')}>
              Verification queue
            </Button>
            {/* Drafting is `invoice.create`, separate from `invoice.edit` -- an Account
                Executive holds this so a quote can be prepared, and nothing else. */}
            {canCreate && (
              <Button variant="primary" onClick={() => setDrafting(true)}>
                New invoice
              </Button>
            )}
          </>
        }
      />

      <NewInvoiceDialog open={drafting} onClose={() => setDrafting(false)} />

      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search invoice number"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
        <Select
          label="Scope"
          hideLabel
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={(event) => setScope(event.target.value)}
        />
      </div>

      <Table
        caption="Invoice list"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        emptyTitle="No invoices"
        emptyDescription="Nothing matches these filters."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </>
  );
}
